//////////////////////////////////////////////////////////////////////////////
//
//  Logger 유틸리티
//
//  작성자: MyungHwan Kim
//  생성일: 2025-10-24
//
//  설명: 개발/프로덕션 환경별 로깅 유틸리티
//
//////////////////////////////////////////////////////////////////////////////

import { ApiError } from '../types/ApiTypes';

/**
 * Logger 인터페이스
 */
interface Logger {
  debug: (message: string, ...args: any[]) => void;
  info: (message: string, ...args: any[]) => void;
  warn: (message: string, ...args: any[]) => void;
  error: (message: string, error?: any) => void;
  apiRequest: (name: string, url: string, params?: any) => void;
  apiResponse: (name: string, status: number, duration?: number) => void;
  apiError: (name: string, error: any) => void;
}

/**
 * 개발 환경 체크
 */
const isDev = process.env.NODE_ENV === 'development';

/**
 * 프로덕션 환경에서는 에러 트래킹 서비스로 전송
 * (Sentry, Firebase Crashlytics 등)
 */
const sendToErrorTracking = (message: string, error?: any) => {
  // TODO: 에러 트래킹 서비스 연동
  // Sentry.captureException(error);
};

/**
 * Logger 구현
 */
export const logger: Logger = {
  /**
   * 디버그 로그 (개발 환경에서만)
   */
  debug: (message: string, ...args: any[]) => {
    if (isDev) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  },

  /**
   * 정보 로그
   */
  info: (message: string, ...args: any[]) => {
    if (isDev) {
      console.log(`ℹ️ [INFO] ${message}`, ...args);
    }
  },

  /**
   * 경고 로그
   */
  warn: (message: string, ...args: any[]) => {
    if (isDev) {
      console.warn(`⚠️ [WARN] ${message}`, ...args);
    }
  },

  /**
   * 에러 로그
   */
  error: (message: string, error?: any) => {
    if (isDev) {
      console.error(`❌ [ERROR] ${message}`, error);
    } else {
      // 프로덕션: 에러 트래킹 서비스로 전송
      sendToErrorTracking(message, error);
    }
  },

  /**
   * API 요청 로그
   */
  apiRequest: (name: string, url: string, params?: any) => {
    if (isDev) {
      console.group(`📤 API Request: ${name}`);
      console.log('URL:', url);
      if (params) {
        console.log('Params:', params);
      }
      console.groupEnd();
    }
  },

  /**
   * API 응답 로그
   */
  apiResponse: (name: string, status: number, duration?: number) => {
    if (isDev) {
      const emoji = status >= 200 && status < 300 ? '✅' : '⚠️';
      console.log(
        `${emoji} API Response: ${name} - Status: ${status}${
          duration ? ` (${duration}ms)` : ''
        }`,
      );
    }
  },

  /**
   * API 에러 로그 (상세)
   */
  apiError: (name: string, error: any) => {
    if (isDev) {
      console.group(`🔴 API Error: ${name}`);
      console.log('Code:', error.code);
      console.log('Message:', error.message);
      if (error.response) {
        console.log('Status:', error.response.status);
        console.log('Data:', error.response.data);
      }
      console.log('Full Error:', error);
      console.groupEnd();
    } else {
      // 프로덕션: 에러 트래킹
      sendToErrorTracking(`API Error: ${name}`, error);
    }
  },
};

export default logger;

